import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (params.get("email") && !verified) {
      // Re-trigger send if user just registered (but server already sent on register)
    }
  }, [params, verified]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { email: email.toLowerCase().trim(), code: code.trim() });
      setVerified(true);
      toast.success("Email confirmado!");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!email) { toast.error("Informe o email"); return; }
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: email.toLowerCase().trim() });
      toast.success("Novo código enviado");
    } catch (err) { toast.error("Falha ao reenviar"); }
    finally { setResending(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="verify-email-page">
      <Card className="p-6 rounded-2xl border-blue-200">
        <div className="flex items-center gap-2 text-blue-900 mb-2"><Mail size={24}/><h1 className="font-display text-2xl font-bold">Confirme seu email</h1></div>
        {verified ? (
          <div className="text-center py-8">
            <CheckCircle2 className="text-emerald-600 mx-auto mb-2" size={48}/>
            <div className="font-display text-xl font-bold">Email confirmado!</div>
            <div className="text-sm text-stone-500 mt-1">Redirecionando…</div>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-600 mb-4">Enviamos um código de 6 dígitos para seu email. Digite abaixo para confirmar.</p>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" data-testid="verify-email"/></div>
              <div><Label>Código (6 dígitos)</Label><Input required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="rounded-xl mt-1 font-mono text-2xl text-center tracking-widest" maxLength={6} data-testid="verify-code"/></div>
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="verify-submit">
                {busy ? "Verificando..." : "Confirmar email"}
              </Button>
              <Button type="button" variant="outline" onClick={resend} disabled={resending} className="w-full rounded-xl" data-testid="verify-resend">
                <RefreshCw size={14} className="mr-1"/>{resending ? "Reenviando..." : "Reenviar código"}
              </Button>
            </form>
            <Link to="/login" className="block text-center text-sm text-blue-700 mt-4 hover:underline">Voltar ao login</Link>
          </>
        )}
      </Card>
    </div>
  );
}
